/* Admin (stage 5): the shell and the shared pieces every admin board uses, from AdminToday.dc.html and
   the other admin boards. Master brand only: white, the ink, greys as tints of it, gold for small
   accents that never carry text. Every colour is a token (styles/tokens.css). Under 760px the
   sidebar becomes a Menu button and tables become stacked rows (Adam, 4 Oct, answer 5). */

/* ---- The frame -------------------------------------------------------------------------------- */
.ad-app {
  min-height: 100vh; display: flex; background: var(--shell-bg); color: var(--shell-ink);
  font-family: var(--shell-font);
}
.ad-app a:focus-visible, .ad-app button:focus-visible, .ad-app input:focus-visible,
.ad-app select:focus-visible, .ad-app textarea:focus-visible, .ad-app [tabindex]:focus-visible {
  outline: 2px solid var(--admin-focus); outline-offset: 2px;
}
.ad-app select, .ad-app textarea { font-family: inherit; }

.ad-side { width: 248px; flex-shrink: 0; background: var(--admin-side); color: var(--on-dark); }
.ad-side-inner {
  position: sticky; top: 0; height: 100vh; overflow-y: auto;
  display: flex; flex-direction: column; justify-content: space-between; gap: 24px; padding: 26px 14px;
}
.ad-side-top { display: flex; flex-direction: column; gap: 30px; }

.ad-mark { display: flex; align-items: center; gap: 9px; padding: 0 12px; color: var(--on-dark); text-decoration: none; }
.ad-mark-name { font-weight: 800; font-size: 17px; }
.ad-mark-dot { width: 5px; height: 5px; border-radius: 3px; background: var(--shell-gold); flex-shrink: 0; }
.ad-mark-sub { font-weight: 500; font-size: 14px; color: var(--on-dark-70); }

.ad-nav { display: flex; flex-direction: column; gap: 2px; }
.ad-nav-item {
  display: flex; align-items: center; gap: 12px; height: 42px; padding: 0 12px; border-radius: 9px;
  color: var(--on-dark-74); font-size: 15px; font-weight: 600; text-decoration: none;
}
.ad-nav-item:hover { background: var(--on-dark-06); color: var(--on-dark); }
.ad-nav-item[aria-current="page"] {
  background: var(--on-dark-10); color: var(--on-dark); box-shadow: inset 3px 0 0 var(--shell-gold); font-weight: 700;
}
.ad-nav-rule { height: 1px; background: var(--on-dark-10); margin: 12px 8px; }
.ad-icon { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.75; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }

.ad-me { display: flex; align-items: center; gap: 10px; padding: 0 12px; }
.ad-me-ring {
  width: 34px; height: 34px; border-radius: 17px; border: 1.5px solid var(--shell-gold); flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px;
}
.ad-me-text { display: flex; flex-direction: column; min-width: 0; }
.ad-me-name { font-size: 14px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ad-me-role { font-size: 12px; color: var(--on-dark-60); }

.ad-main { flex-grow: 1; min-width: 0; padding: 36px 48px 64px; display: flex; flex-direction: column; gap: 22px; }

/* The phone's top bar and its menu */
.ad-top { position: sticky; top: 0; z-index: 30; background: var(--admin-side); color: var(--on-dark); }
.ad-topbar { height: 56px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 0 16px; }
.ad-topbar .ad-mark { padding: 0; }
.ad-menu-button {
  height: 38px; padding: 0 14px; border-radius: 9px; border: 1px solid var(--on-dark-22); background: transparent;
  color: var(--on-dark); font-weight: 700; font-size: 14px; display: inline-flex; align-items: center; gap: 8px;
}
.ad-menu-panel {
  position: absolute; top: 100%; left: 0; right: 0; max-height: calc(100vh - 56px); overflow-y: auto;
  background: var(--admin-side); border-top: 1px solid var(--on-dark-10); padding: 10px 12px 20px;
  display: flex; flex-direction: column; gap: 18px; box-shadow: 0 18px 40px var(--admin-dialog-shadow);
}
/* The phone's second row for owner and admin: Today, Masterminds and People, the open 1 with a thin gold bar under it
   (gold never carries text) */
.ad-quick { display: flex; border-top: 1px solid var(--on-dark-10); }
.ad-quick-item {
  flex: 1 1 0; min-width: 0; display: flex; align-items: center; justify-content: center; height: 44px; padding: 0 8px;
  color: var(--on-dark-74); font-size: 14px; font-weight: 600; text-decoration: none; white-space: nowrap;
}
.ad-quick-item[aria-current="page"] { color: var(--on-dark); font-weight: 700; box-shadow: inset 0 -3px 0 var(--shell-gold); }
.ad-top:has(.ad-quick) .ad-menu-panel { max-height: calc(100vh - 101px); }

/* ---- Page header, crumbs, notes ---------------------------------------------------------------- */
.ad-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px 24px; flex-wrap: wrap; }
.ad-head-text { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.ad-eyebrow { font-size: 13px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--shell-ink-60); }
/* the boards' 32px heading sits at Manrope's own line height */
.ad-h1 { margin: 0; font-size: 32px; font-weight: 800; letter-spacing: -0.01em; line-height: normal; overflow-wrap: anywhere; }
.ad-head-lede { margin: 0; font-size: 15px; line-height: 1.5; color: var(--shell-ink-66); }
.ad-head-aside { font-size: 14px; line-height: 1.5; color: var(--shell-ink-62); }
.ad-head-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.ad-crumbs { font-size: 14px; color: var(--shell-ink-60); }
.ad-crumbs a { color: var(--shell-ink-60); }
.ad-lede { margin: 0; font-size: 15px; line-height: 1.55; color: var(--shell-ink-72); max-width: 720px; }

.ad-note {
  display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 10px;
  background: var(--shell-ink-03); border: 1px solid var(--shell-ink-08); font-size: 13px; line-height: 1.5; color: var(--shell-ink-72);
}
.ad-note .ad-icon { stroke: var(--shell-ink); }

/* ---- Buttons ------------------------------------------------------------------------------------ */
.ad-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 42px; padding: 0 16px;
  border-radius: 9px; border: 1px solid transparent; font-weight: 700; font-size: 14px; text-decoration: none;
  white-space: nowrap; cursor: pointer; line-height: 1;
}
.ad-btn--ink { background: var(--shell-ink); color: var(--shell-on-ink); }
.ad-btn--ink:hover { color: var(--shell-on-ink); }
.ad-btn--line { background: var(--shell-bg); color: var(--shell-ink); border-color: var(--shell-ink-16); }
.ad-btn--line:hover { background: var(--shell-ink-03); }
.ad-btn--quiet { background: transparent; color: var(--shell-ink); padding: 0 4px; text-decoration: underline; }
.ad-btn--sm { height: 34px; padding: 0 12px; border-radius: 8px; font-size: 13px; }
.ad-btn--xs { height: 28px; padding: 0 10px; border-radius: 7px; font-size: 12px; }
.ad-btn:disabled, .ad-btn[aria-disabled="true"] { opacity: 0.55; cursor: default; }

/* ---- Cards ------------------------------------------------------------------------------------- */
.ad-card { border-radius: 14px; border: 1px solid var(--shell-ink-10); background: var(--shell-bg); overflow: hidden; }
.ad-card-head {
  padding: 18px 22px; display: flex; align-items: baseline; justify-content: space-between; gap: 6px 12px;
  border-bottom: 1px solid var(--shell-ink-08); flex-wrap: wrap;
}
.ad-card-title { margin: 0; font-size: 19px; font-weight: 800; }
.ad-card-aside { font-size: 13px; color: var(--shell-ink-60); }
.ad-card-body { padding: 20px 22px; display: flex; flex-direction: column; gap: 14px; }

/* ---- Flash: what just happened, with the gold bar (the boards' green bar is a brand leak, so gold) */
.ad-flash {
  display: flex; align-items: center; justify-content: space-between; gap: 12px 16px; padding: 12px 16px; border-radius: 10px;
  background: var(--shell-ink-03); border: 1px solid var(--shell-ink-14); box-shadow: inset 4px 0 0 var(--shell-gold);
}
.ad-flash-text { font-size: 14px; font-weight: 700; line-height: 1.5; }
.ad-flash-actions { display: flex; gap: 8px; flex-shrink: 0; align-items: center; }

/* ---- Chips, dots, tags, status pills -------------------------------------------------------------- */
.ad-chips { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.ad-chips-label { font-size: 13px; color: var(--shell-ink-60); margin-right: 4px; }
.ad-chip {
  height: 32px; padding: 0 12px; border-radius: 16px; border: 1px solid var(--shell-ink-16); background: var(--shell-bg);
  color: var(--shell-ink); font-size: 13px; font-weight: 600; display: inline-flex; align-items: center; gap: 7px; white-space: nowrap;
}
.ad-chip--tall { height: 34px; border-radius: 17px; font-weight: 700; }
.ad-chip[aria-pressed="true"] { background: var(--shell-ink); color: var(--shell-on-ink); border-color: var(--shell-ink); }
.ad-chip:disabled { opacity: 0.55; cursor: default; }
.ad-dot { width: 8px; height: 8px; border-radius: 4px; flex-shrink: 0; display: inline-block; }
.ad-dot--ringed { box-shadow: 0 0 0 1px var(--shell-ink-30); }
.ad-tag {
  height: 24px; padding: 0 8px; border-radius: 6px; background: var(--shell-ink-05); font-size: 12px; font-weight: 600;
  display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
}
.ad-pill {
  height: 26px; padding: 0 10px; border-radius: 13px; border: 1px solid var(--shell-ink-06); background: var(--shell-ink-06);
  color: var(--shell-ink); font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; white-space: nowrap; justify-self: start;
}
.ad-pill--attention { background: var(--shell-bg); border-color: var(--shell-ink); }
.ad-pill--paused { background: var(--shell-bg); border-color: var(--shell-ink-30); }
.ad-pill--ended { background: var(--shell-bg); border-color: var(--shell-ink-12); color: var(--shell-ink-60); }

/* ---- Tabs: where they don't fit on 1 line (a phone, a narrow window) they wrap onto the next, so
   every tab shows and none starts off screen -------------------------------------------------------- */
.ad-tabs { display: flex; flex-wrap: wrap; gap: 0 28px; border-bottom: 1px solid var(--shell-ink-10); }
.ad-tab {
  padding: 10px 0 12px; border: none; border-bottom: 2px solid transparent; background: none; color: var(--shell-ink-60);
  font-weight: 800; font-size: 15px; text-decoration: none; white-space: nowrap; flex-shrink: 0;
}
.ad-tab:hover { color: var(--shell-ink); }
.ad-tab[aria-current="page"], .ad-tab[aria-selected="true"] { color: var(--shell-ink); border-bottom-color: var(--shell-ink); }

/* ---- Tables: a grid of rows at laptop width, stacked rows on a phone -------------------------------- */
.ad-table { border-radius: 14px; border: 1px solid var(--shell-ink-10); overflow: hidden; background: var(--shell-bg); }
.ad-table--flush { border: none; border-radius: 0; }
.ad-tr {
  display: grid; grid-template-columns: var(--ad-cols); gap: 16px; align-items: center; padding: 12px 22px;
  border-bottom: 1px solid var(--shell-ink-06);
}
.ad-tbody .ad-tr:last-child { border-bottom: none; }
.ad-thead .ad-tr {
  background: var(--shell-ink-03); border-bottom: 1px solid var(--shell-ink-08);
  font-size: 12px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--shell-ink-60);
}
.ad-tr--link { cursor: pointer; }
.ad-tr--link:hover, .ad-tbody .ad-tr:hover { background: var(--shell-ink-025); }
.ad-td { min-width: 0; font-size: 14px; line-height: 1.5; overflow-wrap: anywhere; }
.ad-td--end { justify-self: end; }
.ad-td-main { font-size: 15px; font-weight: 700; color: var(--shell-ink); }
.ad-td-sub { display: block; font-size: 13px; color: var(--shell-ink-60); }
.ad-td-quiet { font-size: 13px; color: var(--shell-ink-62); }

/* ---- Confirming in place, and dialogs ------------------------------------------------------------- */
.ad-confirm {
  padding: 14px 16px; border-radius: 10px; background: var(--shell-ink-04);
  display: flex; align-items: center; justify-content: space-between; gap: 12px 16px;
}
.ad-confirm-text { font-size: 14px; line-height: 1.5; }
.ad-confirm-error { display: block; margin-top: 6px; }
.ad-tr-after { padding: 0 22px 14px; border-bottom: 1px solid var(--shell-ink-06); }
.ad-confirm-actions { display: flex; gap: 8px; flex-shrink: 0; }

.ad-veil {
  position: fixed; inset: 0; z-index: 50; background: var(--shell-ink-45); overflow-y: auto;
  display: flex; justify-content: center; align-items: flex-start; padding: 96px 16px 32px;
}
.ad-dialog {
  width: min(600px, 100%); padding: 28px; border-radius: 16px; background: var(--shell-bg); color: var(--shell-ink);
  box-shadow: 0 24px 64px var(--admin-dialog-shadow); display: flex; flex-direction: column; gap: 18px; font-family: var(--shell-font);
}
.ad-dialog--wide { width: min(820px, 100%); }
.ad-dialog-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.ad-dialog-title { margin: 0; font-size: 22px; font-weight: 800; }
/* a dialog's eyebrow over its heading (Dialog's eyebrow; Referrals, 13 Oct) */
.ad-dialog-heads { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.ad-dialog-foot { display: flex; align-items: center; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
.ad-close {
  width: 34px; height: 34px; border-radius: 17px; border: 1px solid var(--shell-ink-16); background: var(--shell-bg);
  color: var(--shell-ink); display: flex; align-items: center; justify-content: center; flex-shrink: 0; padding: 0;
}

/* ---- Forms ------------------------------------------------------------------------------------- */
.ad-form { display: flex; flex-direction: column; gap: 16px; }
.ad-row { display: grid; grid-template-columns: repeat(var(--ad-row-cols, 2), minmax(0, 1fr)); gap: 12px; }
.ad-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; border: none; padding: 0; margin: 0; }
.ad-label { font-size: 13px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--shell-ink-60); padding: 0; }
.ad-label--plain { text-transform: none; letter-spacing: 0; font-size: 14px; font-weight: 700; color: var(--shell-ink); }
.ad-input, .ad-select, .ad-textarea {
  width: 100%; height: 42px; padding: 0 12px; border-radius: 9px; border: 1px solid var(--shell-ink-16);
  background: var(--shell-bg); font-size: 14px; color: var(--shell-ink);
}
.ad-textarea { height: auto; min-height: 76px; padding: 10px 12px; line-height: 1.5; resize: vertical; }
.ad-input::placeholder, .ad-textarea::placeholder { color: var(--shell-ink-60); }
.ad-input[aria-invalid="true"], .ad-select[aria-invalid="true"], .ad-textarea[aria-invalid="true"] {
  border-color: var(--admin-wrong); box-shadow: 0 0 0 1px var(--admin-wrong);
}
.ad-input[readonly] { background: var(--shell-ink-03); }
.ad-search { position: relative; width: 340px; max-width: 100%; flex-shrink: 0; }
.ad-search .ad-icon { position: absolute; left: 13px; top: 13px; width: 16px; height: 16px; stroke: var(--shell-ink-60); stroke-width: 2; }
.ad-search .ad-input { padding-left: 38px; }
.ad-hint { font-size: 12px; line-height: 1.5; color: var(--shell-ink-60); margin: 0; }
.ad-error { font-size: 13px; font-weight: 700; line-height: 1.5; color: var(--shell-ink); margin: 0; }
.ad-check { display: flex; gap: 10px; align-items: center; font-size: 14px; line-height: 1.45; cursor: pointer; }
.ad-check input { width: 17px; height: 17px; accent-color: var(--shell-ink); flex-shrink: 0; margin: 0; }

.ad-switch-row { display: flex; align-items: center; gap: 10px; }
.ad-switch-label { font-size: 12px; font-weight: 700; color: var(--shell-ink-60); }
.ad-switch {
  width: 42px; height: 24px; border-radius: 12px; border: none; background: var(--shell-ink-20); position: relative; padding: 0; flex-shrink: 0;
}
.ad-switch[aria-checked="true"] { background: var(--shell-ink); }
.ad-switch-knob { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 9px; background: var(--shell-bg); transition: left 0.15s; }
.ad-switch[aria-checked="true"] .ad-switch-knob { left: 21px; }
.ad-switch:disabled { opacity: 0.55; }

.ad-options { display: flex; flex-direction: column; gap: 8px; }
.ad-option {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; border-radius: 10px;
  border: 1.5px solid var(--shell-ink-16); background: var(--shell-bg); color: var(--shell-ink); text-align: left; font-size: 14px; font-weight: 700;
}
.ad-option[aria-checked="true"] { border-color: var(--shell-ink); background: var(--shell-ink-03); }
.ad-option:disabled { opacity: 0.55; cursor: default; }
.ad-option-name { display: flex; align-items: center; gap: 10px; }
.ad-option-note { font-size: 12px; font-weight: 500; color: var(--shell-ink-60); text-align: right; }
.ad-options--row { flex-direction: row; flex-wrap: wrap; }
.ad-options--row .ad-option { flex: 1 1 140px; justify-content: center; height: 40px; padding: 0 12px; font-size: 13px; text-align: center; }

.ad-date-words { font-size: 12px; color: var(--shell-ink-60); }

/* ---- Empty, loading, error ------------------------------------------------------------------------ */
.ad-empty { padding: 22px; font-size: 14px; line-height: 1.55; color: var(--shell-ink-60); margin: 0; }
.ad-empty-action { padding: 0 22px 22px; }
.ad-loading { padding: 22px; font-size: 14px; color: var(--shell-ink-60); margin: 0; }
.ad-problem {
  padding: 16px 20px; border-radius: 12px; border: 1px solid var(--shell-ink-14); box-shadow: inset 4px 0 0 var(--shell-gold);
  display: flex; align-items: center; justify-content: space-between; gap: 12px 16px; flex-wrap: wrap;
}
.ad-problem-text { margin: 0; font-size: 14px; font-weight: 700; line-height: 1.5; }

/* ---- The holding panel and the 2-step screens ---------------------------------------------------------- */
.ad-holding .ad-card-body { gap: 10px; }
.ad-mfa { display: flex; flex-direction: column; gap: 16px; max-width: 640px; }
.ad-mfa-qr { width: 200px; height: 200px; border: 1px solid var(--shell-ink-12); border-radius: 12px; padding: 8px; background: var(--shell-bg); }
.ad-mfa-key { margin: 0; font-size: 15px; line-height: 1.55; color: var(--shell-ink-66); }
.ad-mfa-tip { margin: 0; font-size: 14px; line-height: 1.55; color: var(--shell-ink-66); }
.ad-mfa-secret { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; color: var(--shell-ink); word-break: break-all; }
.ad-mfa-form { display: flex; flex-direction: column; gap: 8px; max-width: 360px; }
.ad-mfa-code {
  height: 56px; padding: 0 16px; border-radius: 10px; border: 1px solid var(--shell-ink-18); background: var(--shell-bg);
  font-size: 24px; letter-spacing: 0.3em; font-weight: 700; color: var(--shell-ink); width: 100%;
}

/* ---- The member side: a staff member's account menu, with Admin in it ----------------------------- */
button.sh-avatar, button.ph-avatar { background: transparent; padding: 0; font-family: inherit; }
.sh-menu { top: 56px; right: 0; width: 220px; border-radius: 12px; gap: 2px; z-index: 40; }
.sh-menu-item {
  display: flex; align-items: center; height: 42px; padding: 0 12px; border-radius: 8px;
  font-size: 15px; font-weight: 600; color: var(--shell-ink); text-decoration: none;
}
.sh-menu-item:hover { background: var(--shell-ink-04); }
.ph-panel--account { padding: 4px 16px 8px; }

/* ---- View as member: the bar on every page while staff view as a member --------------------------- */
.va-bar {
  background: var(--shell-ink); color: var(--on-dark); font-family: var(--shell-font);
  display: flex; align-items: center; justify-content: space-between; gap: 12px 20px;
  padding: 12px clamp(16px, 4.45vw, 64px); position: relative; z-index: 45; border-bottom: 1px solid var(--on-dark-22);
}
.va-bar-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.va-bar-line { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 700; line-height: 1.45; }
.va-bar-dot { width: 7px; height: 7px; border-radius: 4px; background: var(--shell-gold); flex-shrink: 0; }
.va-bar-ends { font-size: 13px; line-height: 1.45; color: var(--on-dark-78); padding-left: 17px; }
.va-bar-error { font-size: 13px; font-weight: 700; color: var(--on-dark); padding-left: 17px; margin: 0; }
.va-bar-stop {
  height: 36px; padding: 0 14px; border-radius: 8px; border: none; background: var(--shell-bg); color: var(--shell-ink);
  font-weight: 700; font-size: 13px; flex-shrink: 0; font-family: inherit;
}
.va-bar-stop:focus-visible { outline: 2px solid var(--shell-gold); outline-offset: 2px; }
.va-gate { display: flex; flex-direction: column; gap: 14px; max-width: 640px; }

/* ---- Phone ------------------------------------------------------------------------------------- */
@media (max-width: 760px) {
  .ad-app { flex-direction: column; }
  .ad-main { padding: 20px 16px 48px; gap: 18px; }
  .ad-h1 { font-size: 26px; }
  .ad-head { align-items: flex-start; }
  .ad-head-actions { width: 100%; }
  .ad-card-head, .ad-card-body { padding-left: 16px; padding-right: 16px; }
  .ad-flash, .ad-confirm, .ad-problem { flex-direction: column; align-items: stretch; }
  .ad-flash-actions, .ad-confirm-actions { flex-wrap: wrap; }
  .ad-tabs { gap: 0 20px; }
  .ad-row { grid-template-columns: minmax(0, 1fr); }
  .ad-search { width: 100%; }
  .ad-input, .ad-select, .ad-textarea { font-size: 16px; }
  .ad-veil { padding: 16px 12px; }
  .ad-dialog { padding: 20px 16px; }

  /* tables: the header row is for screen readers only, and each cell carries its own label */
  .ad-thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
  .ad-tr { display: flex; flex-direction: column; align-items: stretch; gap: 6px; padding: 14px 16px; }
  .ad-td--end { justify-self: auto; }
  .ad-td[data-label]:not([data-label=""])::before {
    content: attr(data-label); display: block; margin-bottom: 2px;
    font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--shell-ink-60);
  }
  .ad-td--primary::before { display: none !important; }

  .va-bar { flex-direction: column; align-items: stretch; }
  .va-bar-stop { width: 100%; height: 40px; }

  /* every control at least 24px to tap on a phone (the stage 5 batch, item 178) */
  .ad-mark { min-height: 24px; }
  .ad-crumbs a { display: inline-flex; align-items: center; min-height: 24px; }
  a.ad-td-main { display: flex; align-items: center; min-height: 24px; }
  .ad-check { min-height: 24px; }
}

/* The owner's quiet line when the day's clear-up didn't finish (stage 6), at the foot of the page */
.ad-quiet-line { margin: 8px 0 0; padding-top: 12px; border-top: 1px solid var(--shell-ink-08); font-size: 12px; line-height: 1.5; color: var(--shell-ink-60); }

/* View as Sam, on a person's page */
.ad-viewas { display: inline-flex; flex-direction: column; gap: 6px; }
@media (max-width: 760px) {
  .ad-tr-after { padding: 0 16px 14px; }
}
.sh-account { position: relative; display: flex; }
